import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { SensingType } from '@components/dashboard/alert-summary/alert-summary.component';
import {
  AlertDialogComponent,
  DialogOption as AlertDialogOption,
} from '@components/shared/alert-dialog/alert-dialog.component';
import {
  isBeforeShipping,
  ShipmentStatus,
  ShipmentStatusDisplayLabel,
} from '@components/shipment-list/shipment-status';
import {
  ShipmentGeofenceViewModel,
  ShipmentLocationViewModel,
} from '@components/shipment-location-list/shipment-location-list-table/shipment-location-list-table.component';
import { ShipmentRouteViewModel } from '@components/shipment-route-list/shipment-route-map/shipment-route-map.component';
import { AccountResponse } from '@entities/account';
import { AlertHistoryResponse } from '@entities/alert';
import {
  ShipmentJudgedReasonType,
  ShipmentResponse,
  SOURCE_PAGE_QUERY_PARAM,
  SourcePage,
} from '@entities/shipment';
import { DEFAULT_RADIUS_METERS } from '@entities/shipment-location';
import { ShipmentRouteResponse } from '@entities/shipment-route';
import { DeviceInfoService } from '@services/api/device-info.service';
import { ShipmentService } from '@services/api/shipment.service';
import { ExportService } from '@services/export.service';
import { CustomValidator } from '@utils/custom-validators';
import { formatShipmentStayDuration } from '@utils/shipment.util';
import { endOfDay, format, formatISO, startOfDay, subDays } from 'date-fns';
import { finalize, firstValueFrom, forkJoin, map, Observable, of } from 'rxjs';
import {
  DialogOption,
  DialogTitle,
  SensorAlertModel,
  ShipmentInfoAlertMapDialogComponent,
  ShipmentModel,
} from './shipment-info-alert-map-dialog/shipment-info-alert-map-dialog.component';
import { ShipmentInfoInProgressComponent } from './shipment-info-in-progress/shipment-info-in-progress.component';
import {
  ShipmentStatusViewModel,
  ShipmentViewModel,
} from './shipment-info-list-table/shipment-info-list-table.component';
import { ShipmentInfoNotReadyComponent } from './shipment-info-not-ready/shipment-info-not-ready.component';
import { ShipmentInfoPreparationViewModel } from './shipment-info-preparation.model';
import { ShipmentInfoReadyComponent } from './shipment-info-ready/shipment-info-ready.component';
import { ShipmentInfoResultComponent } from './shipment-info-result/shipment-info-result.component';

type ShipmentInfoTab = 'notReady' | 'ready' | 'inProgress' | 'result';

const MAX_EXPORT_VIA_LOCATION_COUNT = 20;

type ShipmentResultCsvRow = {
  shipmentName: string;
  shipmentStatus: string;
  deviceName: string;
  departureName: string;
  actualStartDatetime?: string;
  arrivalName: string;
  actualArrivalDatetime?: string;
  /** 経由地1..最大20の可変キー(viaLocationNameN / viaArrivalDatetimeN / viaStayDurationN)を格納するため */
  [key: string]: string | undefined;
};

@Component({
  selector: 'app-shipment-info',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonToggleModule,
    ShipmentInfoNotReadyComponent,
    ShipmentInfoReadyComponent,
    ShipmentInfoInProgressComponent,
    ShipmentInfoResultComponent,
  ],
  templateUrl: './shipment-info.component.html',
  styleUrl: './shipment-info.component.scss',
})
export class ShipmentInfoComponent implements OnInit {
  private readonly resultCsvFixedColumnMap = [
    { key: 'shipmentName', label: '配送名' },
    { key: 'shipmentStatus', label: '配送ステータス' },
    { key: 'deviceName', label: 'デバイス名' },
    { key: 'departureName', label: '出発地名' },
    { key: 'actualStartDatetime', label: '出発実績日時' },
    { key: 'arrivalName', label: '到着地名' },
    { key: 'actualArrivalDatetime', label: '到着実績日時' },
  ];

  /** Not Ready/Ready表示用データ */
  public notReadyShipmentList: ShipmentInfoPreparationViewModel[] = [];
  public readyShipmentList: ShipmentInfoPreparationViewModel[] = [];

  /** 配送中/配送実績表示用データ */
  public inProgressShipmentList: ShipmentViewModel[] = [];
  public resultShipmentList: ShipmentViewModel[] = [];

  /** 日付検索の開始日と終了日の最大間隔 */
  public readonly maxRangeMonths: number = 2;

  /** 配送実績用の期間フィルター */
  public resultDateRangeFormGroup: FormGroup;

  /** 配送実績用の読み込み状態 */
  public resultRouteLoading = false;
  public resultAlertLoading = false;
  public resultSensorAlertLoading = false;

  /** テーブルのデフォルトフィルター値 */
  public defaultFilters: { [key: string]: string | boolean | number } = {};

  /** 編集が可能な権限か */
  public editableRole = false;

  /** タブ選択 */
  public selectedTab: ShipmentInfoTab = 'notReady';
  private sourceShipmentId: string | undefined;
  private shouldSelectTabBySourceShipment = false;
  private inProgressLoaded = false;
  private resultLoaded = false;
  private sourceTabSelected = false;

  constructor(
    private deviceInfoService: DeviceInfoService,
    private shipmentService: ShipmentService,
    private exportService: ExportService,
    private dialog: MatDialog,
    private route: ActivatedRoute,
  ) {
    const defaultEndDate = startOfDay(new Date());
    const defaultStartDate = endOfDay(subDays(defaultEndDate, 7));
    this.resultDateRangeFormGroup = new FormGroup(
      {
        historyFrom: new FormControl(defaultStartDate),
        historyTo: new FormControl(defaultEndDate),
      },
      {
        validators: [
          CustomValidator.startDateBeforeEndDateValidator(
            'historyFrom',
            'historyTo',
          ),
          CustomValidator.maxMonthsValidator(
            'historyFrom',
            'historyTo',
            this.maxRangeMonths,
          ),
        ],
      },
    );
  }

  ngOnInit(): void {
    const queryParams = this.route.snapshot.queryParams;
    const sourcePage = queryParams[SOURCE_PAGE_QUERY_PARAM];
    const shipmentId = queryParams['shipmentId'];
    const shipmentName = queryParams['shipmentName'];
    const resultHistoryFrom = queryParams['resultHistoryFrom'];
    const resultHistoryTo = queryParams['resultHistoryTo'];
    const startDateRange =
      queryParams['startDateRange'] ?? queryParams['arrivalDateRange'];
    const hasResultHistoryRange = this.applyResultHistoryRange(
      resultHistoryFrom,
      resultHistoryTo,
    );

    if (
      !hasResultHistoryRange &&
      (sourcePage === SourcePage.DASHBOARD ||
        sourcePage === SourcePage.DEVICE_INFO)
    ) {
      this.selectedTab = 'inProgress';
      this.shouldSelectTabBySourceShipment = true;
      this.sourceShipmentId = shipmentId;
    }

    if (shipmentName) {
      this.defaultFilters['shipmentName'] = shipmentName;
    }

    if (
      !hasResultHistoryRange &&
      startDateRange &&
      (startDateRange?.length || 0) === 2
    ) {
      this.resultDateRangeFormGroup
        .get('historyFrom')
        ?.setValue(new Date(startDateRange[0]));

      this.resultDateRangeFormGroup
        .get('historyTo')
        ?.setValue(new Date(startDateRange[1]));
    }

    // ログインユーザーの権限を取得する
    const loggedInUser: AccountResponse =
      this.route.snapshot.data['loggedInUser'];
    this.editableRole = ['tenant_admin', 'tenant_editor'].includes(
      loggedInUser.tenant.roleId,
    );

    // 4つのタブの配送設定の一覧を取得する
    this.reloadAllShipmentLists();
  }

  /**
   * 配送一覧(NotReady)の初期化をする
   */
  public loadNotReadyList(): void {
    this.loadPreparationList('notReady', (list) => {
      this.notReadyShipmentList = list;
    });
  }

  /**
   * 配送一覧(Ready)の初期化をする
   */
  public loadReadyList(): void {
    this.loadPreparationList('ready', (list) => {
      this.readyShipmentList = list;
    });
  }

  /**
   * 配送一覧(配送中)の初期化をする
   */
  public loadInProgressList(): void {
    this.findShipmentList('inProgress').subscribe((list) => {
      this.inProgressShipmentList = list;
      this.inProgressLoaded = true;
      this.trySelectTabBySourceShipment();
      this.findShipmentDetail(
        list.map((shipment) => shipment.shipmentId),
        'inProgress',
      );
    });
  }

  /**
   * 配送一覧(実績)の初期化をする
   */
  public loadResultShipmentList(): void {
    const dateFrom = this.resultDateRangeFormGroup.value.historyFrom;
    const dateTo = this.resultDateRangeFormGroup.value.historyTo;
    const dateRange = this.getDateRange(dateFrom, dateTo) ?? [];

    this.findShipmentList('result', dateRange).subscribe((list) => {
      this.resultShipmentList = list;
      this.resultLoaded = true;
      this.trySelectTabBySourceShipment();
      this.findShipmentDetail(
        list.map((shipment) => shipment.shipmentId),
        'result',
      );
    });
  }

  public exportResultShipmentListToCsv(): void {
    try {
      const exportViaLocationCount = this.getExportViaLocationCount();
      const resultCsvColumnMap = this.createResultCsvColumnMap(
        exportViaLocationCount,
      );
      const csvData: ShipmentResultCsvRow[] = this.resultShipmentList.map(
        (shipment) => {
          const firstDevice = shipment.deviceList?.at(0);
          const viaLocationCsvData = this.createViaLocationCsvData(
            shipment,
            exportViaLocationCount,
          );

          return {
            shipmentName: shipment.shipmentName,
            shipmentStatus:
              ShipmentStatusDisplayLabel[shipment.status as ShipmentStatus] ??
              shipment.status,
            deviceName: firstDevice?.deviceName ?? firstDevice?.deviceId ?? '',
            departureName: shipment.route.startLocation?.locationName ?? '',
            actualStartDatetime: shipment.actualStartDatetime,
            arrivalName: shipment.route.arrivalLocation?.locationName ?? '',
            actualArrivalDatetime: shipment.actualArrivalDatetime,
            ...viaLocationCsvData,
          };
        },
      );

      const fileName = `shipment_results_${format(new Date(), 'yyyyMMddHHmmss')}.csv`;
      this.exportService.exportToCsv(
        resultCsvColumnMap,
        csvData,
        fileName,
        true,
        'yyyy-MM-dd HH:mm:ss',
        '',
        'Asia/Tokyo',
      );
    } catch {
      this.openErrorDialog(
        'CSVのエクスポートに失敗しました。\nしばらくしてからもう一度お試しください。',
      );
    }
  }

  /**
   * エクスポート対象全配送の最大経由地件数を取得する（上限20）。
   * @returns CSVで出力する経由地カラム件数
   */
  private getExportViaLocationCount(): number {
    const maxViaLocationCount = this.resultShipmentList.reduce(
      (maxCount, shipment) => {
        const viaLocationCount = shipment.route.viaLocationList?.length ?? 0;
        return Math.max(maxCount, viaLocationCount);
      },
      0,
    );

    return Math.min(maxViaLocationCount, MAX_EXPORT_VIA_LOCATION_COUNT);
  }

  /**
   * 配送実績CSVのカラム定義を作成する。
   * @param viaLocationCount 経由地カラムとして出力する件数
   * @returns CSVカラム定義一覧
   */
  private createResultCsvColumnMap(
    viaLocationCount: number,
  ): { key: string; label: string }[] {
    // 経由地件数を0..20に正規化し、異常値でも出力形式が崩れないようにする。
    const normalizedViaLocationCount = Math.max(
      0,
      Math.min(viaLocationCount, MAX_EXPORT_VIA_LOCATION_COUNT),
    );

    // 経由地名/到着時刻/滞在時間の3列セットを、必要件数分だけ生成する。
    const viaLocationCsvColumnMap = Array.from(
      { length: normalizedViaLocationCount },
      (_, index) => {
        const columnIndex = index + 1;
        return [
          {
            key: this.getViaLocationNameKey(columnIndex),
            label: `経由地名${columnIndex}`,
          },
          {
            key: this.getViaArrivalDatetimeKey(columnIndex),
            label: `到着時刻${columnIndex}`,
          },
          {
            key: this.getViaStayDurationKey(columnIndex),
            label: `滞在時間${columnIndex}`,
          },
        ];
      },
    ).flat();

    return [...this.resultCsvFixedColumnMap, ...viaLocationCsvColumnMap];
  }

  /**
   * 配送実績CSVの経由地関連カラムを生成する。
   * @param shipment 配送情報（経由地・ステータス履歴を含む）
   * @param exportViaLocationCount 経由地カラムとして出力する件数
   * @returns 経由地名/到着時刻/滞在時間のCSVキーと値
   */
  private createViaLocationCsvData(
    shipment: ShipmentViewModel,
    exportViaLocationCount: number,
  ): Record<string, string> {
    // 経由地列を空文字で初期化して、列数を固定する。
    const csvData: Record<string, string> = {};

    for (let index = 1; index <= exportViaLocationCount; index++) {
      csvData[this.getViaLocationNameKey(index)] = '';
      csvData[this.getViaArrivalDatetimeKey(index)] = '';
      csvData[this.getViaStayDurationKey(index)] = '';
    }

    // 経由地を指定件数に制限し、経由地がなければ初期化済みデータを返す。
    const viaLocationList =
      shipment.route.viaLocationList?.slice(0, exportViaLocationCount) ?? [];
    if (viaLocationList.length === 0) {
      return csvData;
    }

    // ステータス履歴を時系列順に並べ、locationIdごとのAtStop/InProgressキューを作る。
    const statusHistory = [...(shipment.statusHistory ?? [])].sort(
      (a, b) => new Date(a.eventAt).getTime() - new Date(b.eventAt).getTime(),
    );
    const atStopQueueByLocationId = new Map<
      string,
      ShipmentStatusViewModel[]
    >();
    const inProgressQueueByLocationId = new Map<
      string,
      ShipmentStatusViewModel[]
    >();

    for (const status of statusHistory) {
      if (!status.locationId) {
        continue;
      }

      if (status.status === ShipmentStatus.AtStop) {
        const queue = atStopQueueByLocationId.get(status.locationId) ?? [];
        queue.push(status);
        atStopQueueByLocationId.set(status.locationId, queue);
      } else if (status.status === ShipmentStatus.InProgress) {
        const queue = inProgressQueueByLocationId.get(status.locationId) ?? [];
        queue.push(status);
        inProgressQueueByLocationId.set(status.locationId, queue);
      }
    }

    // 経由地順に、到着(AtStop)と出発(InProgress)を対応付けてCSV値を埋める。
    viaLocationList.forEach((viaLocation, arrayIndex) => {
      const columnIndex = arrayIndex + 1;
      const locationId = viaLocation.locationId;
      csvData[this.getViaLocationNameKey(columnIndex)] =
        viaLocation.locationName;

      // 同一locationIdの未使用AtStopを1件取り出して到着時刻に設定する。
      const atStopQueue = atStopQueueByLocationId.get(locationId);
      const atStopStatus = atStopQueue?.shift();
      if (!atStopStatus) {
        return;
      }

      csvData[this.getViaArrivalDatetimeKey(columnIndex)] =
        atStopStatus.eventAt;

      // 到着時刻より後の最初のInProgressを取り出し、滞在時間を算出する。
      const inProgressQueue = inProgressQueueByLocationId.get(locationId) ?? [];
      const departureStatus = this.shiftFirstInProgressStatusAfter(
        inProgressQueue,
        atStopStatus.eventAt,
      );
      if (!departureStatus) {
        return;
      }

      csvData[this.getViaStayDurationKey(columnIndex)] =
        formatShipmentStayDuration(
          atStopStatus.eventAt,
          departureStatus.eventAt,
        );
    });

    return csvData;
  }

  /**
   * 指定時刻より後に発生した最初のInProgressをキューから取り出す。
   * @param inProgressQueue locationId単位で時系列に並んだInProgressキュー
   * @param afterEventAt この時刻より後のイベントを探索する基準時刻
   * @returns 条件に合うInProgress。見つからなければundefined
   */
  private shiftFirstInProgressStatusAfter(
    inProgressQueue: ShipmentStatusViewModel[],
    afterEventAt: string,
  ): ShipmentStatusViewModel | undefined {
    // 基準時刻が不正なら算出できないため終了する。
    const afterTimestamp = new Date(afterEventAt).getTime();
    if (Number.isNaN(afterTimestamp)) {
      return;
    }

    // 先頭から順に取り出し、基準時刻より後の最初のイベントを返す。
    while (inProgressQueue.length > 0) {
      const candidate = inProgressQueue.shift();
      if (!candidate) {
        continue;
      }
      const candidateTimestamp = new Date(candidate.eventAt).getTime();
      if (
        !Number.isNaN(candidateTimestamp) &&
        candidateTimestamp > afterTimestamp
      ) {
        return candidate;
      }
    }

    return;
  }
  /**
   * 経由地名CSVカラムのキーを返す。
   * @param index 1始まりの経由地番号
   * @returns `viaLocationName{index}` 形式のキー
   */
  private getViaLocationNameKey(index: number): string {
    return `viaLocationName${index}`;
  }

  /**
   * 経由地到着時刻CSVカラムのキーを返す。
   * @param index 1始まりの経由地番号
   * @returns `viaArrivalDatetime{index}` 形式のキー
   */
  private getViaArrivalDatetimeKey(index: number): string {
    return `viaArrivalDatetime${index}`;
  }

  /**
   * 経由地滞在時間CSVカラムのキーを返す。
   * @param index 1始まりの経由地番号
   * @returns `viaStayDuration{index}` 形式のキー
   */
  private getViaStayDurationKey(index: number): string {
    return `viaStayDuration${index}`;
  }

  /**
   * 4つのタブの配送一覧を再読込する
   */
  private reloadAllShipmentLists(): void {
    this.inProgressLoaded = false;
    this.resultLoaded = false;
    this.sourceTabSelected = false;
    this.loadNotReadyList();
    this.loadReadyList();
    this.loadInProgressList();
    this.loadResultShipmentList();
  }

  private trySelectTabBySourceShipment(): void {
    if (!this.shouldSelectTabBySourceShipment || this.sourceTabSelected) {
      return;
    }

    if (!this.inProgressLoaded || !this.resultLoaded) {
      return;
    }

    if (!this.sourceShipmentId) {
      this.sourceTabSelected = true;
      return;
    }

    const existsInProgress = this.inProgressShipmentList.some(
      (shipment) => shipment.shipmentId === this.sourceShipmentId,
    );
    if (existsInProgress) {
      this.selectedTab = 'inProgress';
      this.sourceTabSelected = true;
      return;
    }

    const existsResult = this.resultShipmentList.some(
      (shipment) => shipment.shipmentId === this.sourceShipmentId,
    );
    if (existsResult) {
      this.selectedTab = 'result';
    }

    this.sourceTabSelected = true;
  }

  private loadPreparationList(
    status: 'notReady' | 'ready',
    applyList: (list: ShipmentInfoPreparationViewModel[]) => void,
  ): void {
    this.findPreparationShipmentList(status).subscribe((list) => {
      applyList(list);
    });
  }

  private findPreparationShipmentList(
    shipmentStatus: 'notReady' | 'ready',
  ): Observable<ShipmentInfoPreparationViewModel[]> {
    return this.findShipmentResponseList(shipmentStatus).pipe(
      map((shipmentList) =>
        shipmentList.map((shipment) => {
          const locationList = shipment.route?.locationList ?? [];
          const departureLocation = locationList.at(0);
          const arrivalLocation = locationList.at(-1);
          const firstDevice = shipment.deviceList?.at(0);

          return {
            shipmentId: shipment.shipmentId,
            shipmentName: shipment.shipmentName,
            departureName: departureLocation?.locationName ?? '',
            arrivalName: arrivalLocation?.locationName ?? '',
            deviceName: firstDevice?.deviceName ?? firstDevice?.deviceId ?? '',
            battery: firstDevice?.batteryPercentage,
            shipment: shipment,
          };
        }),
      ),
    );
  }

  private findShipmentList(
    shipmentStatus: 'inProgress' | 'result',
    dateRange: string[] = [],
  ): Observable<ShipmentViewModel[]> {
    return this.findShipmentResponseList(shipmentStatus, dateRange).pipe(
      map((shipmentList) =>
        shipmentList.map((shipment) => this.toShipmentViewModel(shipment)),
      ),
    );
  }

  public openAlertAndMapDialogFromPreparation(
    shipment: ShipmentInfoPreparationViewModel,
  ): void {
    this.openAlertAndMapDialog(
      this.toShipmentViewModel(shipment.shipment),
      true,
    );
  }

  private findShipmentResponseList(
    shipmentStatus: ShipmentInfoTab,
    dateRange: string[] = [],
  ): Observable<ShipmentResponse[]> {
    const requestDateRange = shipmentStatus === 'result' ? dateRange : [];
    return this.shipmentService.findList(
      requestDateRange,
      requestDateRange,
      requestDateRange,
      requestDateRange,
      undefined,
      undefined,
      shipmentStatus,
    );
  }

  /**
   * 配送詳細ダイアログを開くために必要な情報を取得して表示する
   * @param shipment 対象の配送
   */
  public async openAlertAndMapDialog(
    shipment: ShipmentViewModel,
    hideAlertSections = false,
  ) {
    const dialogTitle = this.getAlertAndMapDialogTitle(this.selectedTab);

    // 配送アラート履歴を取得
    const alertList$ = this.shipmentService
      .findAlertListById(shipment.shipmentId)
      .pipe(
        map((shipmentAlert) =>
          shipmentAlert.sort((a, b) => (a.eventAt > b.eventAt ? -1 : 1)),
        ),
      );

    // デバイスの詳細履歴を取得
    const deviceInfoHistory$ = this.shipmentService.findDeviceInfoHistoryById(
      shipment.shipmentId,
    );

    // アラート履歴を取得
    const sensorAlertHistory$ = this.shipmentService.findSensorAlertHistoryById(
      shipment.shipmentId,
    );

    // デバイスの最新位置を取得
    const deviceList = shipment.deviceList || [];
    const deviceLocationList$ =
      deviceList.length > 0
        ? forkJoin(
            deviceList.map((device) =>
              this.deviceInfoService
                .findByIdLatest(device.deviceId)
                .pipe(map((latestDeviceInfo) => latestDeviceInfo.location)),
            ),
          )
        : of([]);

    // 上記APIを並列で呼び出して取得する
    const result = await firstValueFrom(
      forkJoin({
        shipmentAlert: alertList$,
        deviceInfoHistory: deviceInfoHistory$,
        shipmentSensorAlert: sensorAlertHistory$,
        deviceLocationList: deviceLocationList$,
      }),
    );
    const shipmentAlert = result.shipmentAlert;
    const deviceInfoHistory = result.deviceInfoHistory;
    const shipmentSensorAlert = result.shipmentSensorAlert;
    const location = result.deviceLocationList.at(0);

    // サマリ情報を作成する
    const shipmentSummary = this.createShipmentSummary(
      shipment,
      shipmentSensorAlert,
    );

    const options: DialogOption = {
      dialogTitle,
      shipment: shipmentSummary.shipmentInfo,
      startDatetime: shipment.startDatetime,
      arrivalDatetime: shipment.arrivalDatetime,
      statusHistory: shipment.statusHistory,
      route: shipment.route,
      alert: shipmentAlert,
      sensorAlert: shipmentSummary.sensorAlert,
      location: location,
      deviceInfoHistory: deviceInfoHistory,
      hideAlertSections: hideAlertSections,
    };

    this.dialog.open(ShipmentInfoAlertMapDialogComponent, {
      width: '100%',
      maxWidth: '1500px',
      maxHeight: 'calc(100dvh - 32px)',
      data: options,
    });
  }

  private getAlertAndMapDialogTitle(tab: ShipmentInfoTab): DialogTitle {
    if (tab === 'inProgress' || tab === 'result') {
      return 'アラート/地図';
    }

    return 'デバイス位置';
  }

  private createShipmentSummary(
    shipment: ShipmentViewModel,
    shipmentSensorAlert: AlertHistoryResponse = {},
  ): { shipmentInfo: ShipmentModel; sensorAlert: SensorAlertModel } {
    // デバイスが複数あったらカンマでつないで名前を表示する
    const deviceList = shipment.deviceList || [];
    const deviceName = deviceList
      .map((device) => device.deviceName || device.deviceId)
      .join(',');

    const sensorAlert: SensorAlertModel = {};
    let alertMaxCount = 0;
    let alertMinCount = 0;
    let alertEventCount = 0;
    let warningMaxCount = 0;
    let warningMinCount = 0;

    // センサタイプ別でアラートを格納
    Object.keys(shipmentSensorAlert).forEach((key) => {
      const sensor = shipmentSensorAlert[key as SensingType];

      sensor?.forEach((detail) => {
        if (!sensorAlert.alertDetails) {
          sensorAlert.alertDetails = [];
        }

        sensorAlert.deviceId = detail?.deviceId;
        sensorAlert.deviceName = detail?.deviceName;

        alertMaxCount += detail.countSummary.alertMax;
        alertMinCount += detail.countSummary.alertMin;
        alertEventCount += detail.countSummary.alertEvent;
        warningMaxCount += detail.countSummary.prealertMax;
        warningMinCount += detail.countSummary.prealertMin;

        detail.alertDetails?.forEach((value) => {
          sensorAlert?.alertDetails?.push({
            sensor: key,
            unit: this.getUnit(key),
            ...value,
          });
        });
      });
    });

    // 時系列順にソート
    sensorAlert?.alertDetails?.sort((a, b) => {
      const aReportedAt = a.reportedAt ? new Date(a.reportedAt).getTime() : 0;
      const bReportedAt = b.reportedAt ? new Date(b.reportedAt).getTime() : 0;
      return bReportedAt - aReportedAt;
    });

    const shipmentInfo: ShipmentModel = {
      shipmentName: shipment.shipmentName,
      deviceName: deviceName,
      shipmentStatus:
        ShipmentStatusDisplayLabel[shipment.status as ShipmentStatus],
      isBeforeReady: isBeforeShipping(shipment.status as ShipmentStatus),
      countSummary: {
        alertMaxCount,
        alertMinCount,
        alertEventCount,
        warningMaxCount,
        warningMinCount,
      },
    };

    return { shipmentInfo, sensorAlert };
  }

  /**
   * 配送ステータス更新の確認ダイアログを表示する
   * @param message 確認メッセージ
   * @returns 操作が確定された場合はtrue
   */
  private async confirmShipmentOperation(message: string): Promise<boolean> {
    const options: AlertDialogOption = {
      type: 'warn',
      title: 'Confirm',
      message: message,
      operation: '更新',
    };

    return await firstValueFrom(
      this.dialog
        .open(AlertDialogComponent, {
          width: '600px',
          data: options,
        })
        .afterClosed(),
    );
  }

  /**
   * エラーダイアログを表示する
   * @param message 表示メッセージ
   * @param width ダイアログ幅
   */
  private openErrorDialog(message: string, width = '600px'): void {
    const alertDialogOptions: AlertDialogOption = {
      type: 'error',
      message: message,
    };
    this.dialog.open(AlertDialogComponent, {
      width: width,
      data: alertDialogOptions,
    });
  }

  /**
   * 配送ステータスを準備完了にする
   * @param shipmentId 配送設定Id
   */
  public async updateShipmentStatusReady(shipmentId: string) {
    const confirmed = await this.confirmShipmentOperation(
      `選択した配送を準備完了としますが、よろしいですか。`,
    );

    if (!confirmed) {
      return;
    }

    this.shipmentService.updateToReady(shipmentId).subscribe({
      next: (result) => {
        const judgedReason = result?.judgedReasons?.[shipmentId];
        if (
          judgedReason === ShipmentJudgedReasonType.DEVICE_NOT_AT_STARTING_POINT
        ) {
          const message =
            '出発地のジオフェンス内にデバイスが無いため、\n準備完了にできませんでした。';
          this.openErrorDialog(message);
        } else if (
          judgedReason === ShipmentJudgedReasonType.UNEXPECTED_DEVICE_STATUS
        ) {
          const message =
            'デバイスステータスが「READY」でないため、\n準備完了にできませんでした。';
          this.openErrorDialog(message);
        } else {
          // 配送情報一覧を再取得する
          this.reloadAllShipmentLists();
        }
      },
      error: () => {
        const message =
          '準備完了にできませんでした。\nしばらくしてからもう一度お試しください。';
        this.openErrorDialog(message);
      },
    });
  }

  /**
   * 配送ステータスを配送中断にする
   * @param shipmentId 配送設定Id
   */
  public async updateShipmentStatusInterrupted(shipmentId: string) {
    const confirmed = await this.confirmShipmentOperation(
      `選択した配送を配送中断としますが、よろしいですか。`,
    );

    if (!confirmed) {
      return;
    }

    this.shipmentService.updateToInterrupt(shipmentId).subscribe({
      next: () => {
        // 配送情報一覧を再取得する
        this.reloadAllShipmentLists();
      },
      error: () => {
        const message =
          '配送中断にできませんでした。\nしばらくしてからもう一度お試しください。';
        this.openErrorDialog(message);
      },
    });
  }

  /**
   * 単位を取得する
   * @param alertType アラート
   * @returns 単位
   */
  private getUnit(alertType: string): string {
    switch (alertType) {
      case 'temperature':
        return '℃';
      case 'humidity':
        return '%';
      case 'pressure':
        return 'hPa';
      case 'light':
        return 'lx';
      case 'tilt':
        return '°';
      default:
        return '';
    }
  }

  private getDateRange(
    dateFrom: Date | undefined | null,
    dateTo: Date | undefined | null,
  ): string[] | undefined {
    if (
      dateFrom == undefined ||
      dateFrom == null ||
      dateTo === undefined ||
      dateTo === null
    ) {
      return;
    }

    const formattedDateFrom = formatISO(startOfDay(dateFrom));
    const formattedDateTo = formatISO(endOfDay(dateTo));
    return [formattedDateFrom, formattedDateTo];
  }

  public getResultHistoryFromQuery(): string {
    const historyFrom = this.resultDateRangeFormGroup.value.historyFrom;
    if (!(historyFrom instanceof Date) || Number.isNaN(historyFrom.getTime())) {
      return '';
    }

    return historyFrom.toISOString();
  }

  public getResultHistoryToQuery(): string {
    const historyTo = this.resultDateRangeFormGroup.value.historyTo;
    if (!(historyTo instanceof Date) || Number.isNaN(historyTo.getTime())) {
      return '';
    }

    return historyTo.toISOString();
  }

  private applyResultHistoryRange(
    resultHistoryFrom?: string,
    resultHistoryTo?: string,
  ): boolean {
    if (!resultHistoryFrom || !resultHistoryTo) {
      return false;
    }

    const historyFromDate = new Date(resultHistoryFrom);
    const historyToDate = new Date(resultHistoryTo);
    if (
      Number.isNaN(historyFromDate.getTime()) ||
      Number.isNaN(historyToDate.getTime())
    ) {
      return false;
    }

    this.resultDateRangeFormGroup.get('historyFrom')?.setValue(historyFromDate);
    this.resultDateRangeFormGroup.get('historyTo')?.setValue(historyToDate);
    this.selectedTab = 'result';
    return true;
  }

  /**
   * 配送レスポンスを画面表示用のViewModelへ変換する
   * @param shipment 配送レスポンス
   * @returns 配送ViewModel
   */
  private toShipmentViewModel(shipment: ShipmentResponse): ShipmentViewModel {
    const route = this.createRoute(shipment.route);
    this.convertGeometryIntoGeofence(shipment, route);

    return {
      shipmentId: shipment.shipmentId,
      shipmentName: shipment.shipmentName,
      alertDetectionOffset: shipment?.alertDetectionOffset,
      status: shipment.status === 'NotReady' ? 'Create' : shipment.status,
      startDatetime: shipment.startDatetime,
      actualStartDatetime: shipment.actualStartDatetime,
      arrivalDatetime: shipment.arrivalDatetime,
      actualArrivalDatetime: shipment.actualArrivalDatetime,
      deviceList: shipment.deviceList,
      route: route,
      sensorAlertStartTime: shipment?.sensorAlertStartTime,
      mapHistoryFrom: shipment?.mapHistoryFrom,
    };
  }

  /**
   * APIレスポンスのルート情報を画面表示用のルートへ変換する
   * @param route ルートレスポンス
   * @returns ルートViewModel
   */
  private createRoute(route: ShipmentRouteResponse): ShipmentRouteViewModel {
    const locations = (route.locationList ?? []).map((location) => {
      let geofence: ShipmentGeofenceViewModel | undefined;
      if (location.geometry?.geofenceType === 'circle') {
        geofence = {
          geofenceType: 'circle',
          circleRadius: location.geometry?.circleRadius || 0,
          circleRadiusUnit: '',
          circleRadiusInMeters: 10,
        };
      } else if (location.geometry?.geofenceType === 'polygon') {
        geofence = {
          geofenceType: 'polygon',
          polygonList: location.geometry?.polygonList || [],
        };
      }

      return { ...location, geofence: geofence };
    });

    // 位置情報を分ける
    let startLocation;
    if (locations.length >= 1) {
      startLocation = locations[0];
    }
    let arrivalLocation;
    if (locations.length >= 2) {
      arrivalLocation = locations[locations.length - 1];
    }
    let viaLocationList: ShipmentLocationViewModel[] = [];
    if (locations.length >= 3) {
      viaLocationList = locations.slice(1, locations.length - 1);
    }

    return {
      routeId: route.routeId,
      routeName: route.routeName,
      startLocation: startLocation,
      viaLocationList: viaLocationList,
      arrivalLocation: arrivalLocation,
      permanent: route.permanent,
      isUse: route.isUse,
    };
  }

  private convertGeometryIntoGeofence(
    shipment: ShipmentResponse,
    route: ShipmentRouteViewModel,
  ): void {
    // ジオメトリ情報をジオフェンス情報に変換する
    if (route.startLocation) {
      const geometry = shipment.route.locationList[0].geometry;
      if (geometry?.geofenceType === 'circle') {
        route.startLocation.geofence = {
          geofenceType: 'circle',
          circleRadius: geometry.circleRadius || 0,
          circleRadiusUnit: geometry.circleRadiusUnit ?? 'm',
          circleRadiusInMeters:
            geometry.circleRadiusInMeters ?? DEFAULT_RADIUS_METERS,
        };
      } else if (geometry?.geofenceType === 'polygon') {
        route.startLocation.geofence = {
          geofenceType: 'polygon',
          polygonList: geometry?.polygonList || [],
        };
      }
    }
    if (route.viaLocationList) {
      const locationList = shipment.route.locationList;
      const viaLocationList = locationList.slice(1, locationList.length - 1);

      viaLocationList.forEach((viaLocation, index) => {
        const geometry = viaLocation.geometry;
        if (geometry?.geofenceType === 'circle') {
          route.viaLocationList![index].geofence = {
            geofenceType: 'circle',
            circleRadius: geometry.circleRadius || 0,
            circleRadiusUnit: geometry.circleRadiusUnit ?? 'm',
            circleRadiusInMeters:
              geometry.circleRadiusInMeters ?? DEFAULT_RADIUS_METERS,
          };
        } else if (geometry?.geofenceType === 'polygon') {
          route.viaLocationList![index].geofence = {
            geofenceType: 'polygon',
            polygonList: geometry?.polygonList || [],
          };
        }
      });
    }
    if (route.arrivalLocation) {
      const geometry =
        shipment.route.locationList[shipment.route.locationList.length - 1]
          .geometry;
      if (geometry?.geofenceType === 'circle') {
        route.arrivalLocation.geofence = {
          geofenceType: 'circle',
          circleRadius: geometry.circleRadius || 0,
          circleRadiusUnit: geometry.circleRadiusUnit ?? 'm',
          circleRadiusInMeters:
            geometry.circleRadiusInMeters ?? DEFAULT_RADIUS_METERS,
        };
      } else if (geometry?.geofenceType === 'polygon') {
        route.arrivalLocation.geofence = {
          geofenceType: 'polygon',
          polygonList: geometry?.polygonList || [],
        };
      }
    }
  }

  /**
   * 配送の各種一覧を取得する
   * @param shipmentIdList
   */
  private findShipmentDetail(
    shipmentIdList: string[],
    targetTab: 'inProgress' | 'result',
  ) {
    if (shipmentIdList.length < 1) {
      return;
    }

    const isResultTab = targetTab === 'result';

    this.runWithOptionalResultLoading(
      isResultTab,
      (loading) => (this.resultRouteLoading = loading),
      this.shipmentService.findStatusList(shipmentIdList, true),
      (shipmentStatusList) => {
        const statusDict: Record<string, ShipmentStatusViewModel[]> = {};
        shipmentStatusList.forEach((shipmentStatus) => {
          statusDict[shipmentStatus.shipmentId] = shipmentStatus.statusList;
        });

        this.updateTargetShipmentList(targetTab, (shipmentList) =>
          shipmentList.map((shipment) => ({
            ...shipment,
            statusHistory: statusDict[shipment.shipmentId] ?? [],
          })),
        );
      },
    );

    this.runWithOptionalResultLoading(
      isResultTab,
      (loading) => (this.resultSensorAlertLoading = loading),
      this.shipmentService.findSensorAlertList(shipmentIdList, true),
      (sensorAlertHistoryList) => {
        const alertMap = new Map(
          sensorAlertHistoryList.map((sensorAlertHistory) => [
            sensorAlertHistory.shipmentId,
            sensorAlertHistory.latestAlertDetail,
          ]),
        );

        this.updateTargetShipmentList(targetTab, (shipmentList) =>
          shipmentList.map((shipment) => {
            const latestAlertDetail = alertMap.get(shipment.shipmentId);
            if (!latestAlertDetail) {
              return shipment;
            }

            const eventType =
              latestAlertDetail.status === 'AlertMax' ||
              latestAlertDetail.status === 'AlertMin' ||
              latestAlertDetail.status === 'Alert'
                ? 'Alert'
                : 'Warn';

            return {
              ...shipment,
              latestSensorAlert: {
                type: eventType,
                eventAt: latestAlertDetail.eventAt,
              },
            };
          }),
        );
      },
    );

    this.runWithOptionalResultLoading(
      isResultTab,
      (loading) => (this.resultAlertLoading = loading),
      this.shipmentService.findLatestAlertList(shipmentIdList, true),
      (alertMap) => {
        this.updateTargetShipmentList(targetTab, (shipmentList) =>
          shipmentList.map((shipment) => ({
            ...shipment,
            latestAlert: alertMap[shipment.shipmentId],
          })),
        );
      },
    );
  }

  private runWithOptionalResultLoading<T>(
    enabled: boolean,
    setLoading: (loading: boolean) => void,
    source$: Observable<T>,
    next: (value: T) => void,
  ): void {
    if (enabled) {
      setLoading(true);
    }

    source$
      .pipe(
        finalize(() => {
          if (enabled) {
            setLoading(false);
          }
        }),
      )
      .subscribe(next);
  }

  private updateTargetShipmentList(
    targetTab: 'inProgress' | 'result',
    update: (list: ShipmentViewModel[]) => ShipmentViewModel[],
  ): void {
    const updatedList = update(this.getTargetShipmentList(targetTab));
    this.setTargetShipmentList(targetTab, updatedList);
  }

  private getTargetShipmentList(
    targetTab: 'inProgress' | 'result',
  ): ShipmentViewModel[] {
    if (targetTab === 'inProgress') {
      return this.inProgressShipmentList;
    }

    return this.resultShipmentList;
  }

  private setTargetShipmentList(
    targetTab: 'inProgress' | 'result',
    shipmentList: ShipmentViewModel[],
  ): void {
    if (targetTab === 'inProgress') {
      this.inProgressShipmentList = shipmentList;
      return;
    }

    this.resultShipmentList = shipmentList;
  }
}
